import { memo, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import LocalPoliceOutlinedIcon from "@mui/icons-material/LocalPoliceOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import { Download } from "@mui/icons-material";
import dayjs from "dayjs";

import {
  downloadCombinedTranscriptInLanguage,
  hasAnyTranscript,
} from "@/shared/utils/transcriptDownload";
import { DownloadTranscriptLanguageModal } from "@/shared/ui/modals/DownloadTranscriptLanguageModal";
import type { VideoData } from "../../shared/interfaces/VideoData";
import { getUserName, useUserDirectory } from "@/features/users/api/userDirectory";

import { usePageHeader } from "../../context/LayoutContext";
import { useOrgId } from "../../shared/auth/orgContext";
import { useToast } from "../../shared/toast";
import { ExtractedInformation } from "../ExtractedInfo/ExtractedInformation";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getEvidenceData, rmsCommitReport } from "@/features/reports/api/reportApi";
import {
  incidentDataQuery,
  reportMetadataQuery,
} from "../../features/reports/api/reportQueries";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import {
  coApprove,
  coRequestChanges,
  type PendingCoComment,
} from "@/features/co-review/api/coReviewApi";
import { coReviewKeys } from "@/features/co-review/api/coReviewQueries";
import { useCoComments } from "@/features/co-review/hooks/useCoComments";
import {
  useReplyToCoComment,
  useResolveCoComment,
  useUnresolveCoComment,
} from "@/features/co-review/api/coReviewMutations";
import { deriveCycleInfo } from "@/features/co-review/model/reviewCycles";
import {
  NARRATIVES_KEY,
  initialStagedFeedback,
  stagedFeedbackReducer,
  toPendingCoComments,
} from "@/features/co-review/model/stagedFeedback";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import { NarrativeWithInlineComments, type PendingNarrativeComment } from "../../components/coReview/NarrativeWithInlineComments";
import { SectionCommentRail } from "../../components/coReview/SectionCommentRail";
import { SectionRejectControl } from "../../components/coReview/SectionRejectControl";
import { ErrorState, SignatureDialog } from "../../shared/ui";
import { downloadReportPdf } from "../../features/reports/api/downloadReportPdf";
import { RejectConfirmationDialog } from "../../components/coReview/RejectConfirmationDialog";
import { ReviewHistoryDialog } from "../../components/coReview/ReviewHistoryDialog";
import { findEasterEggInNarratives } from "@/features/narrative-easter-egg/narrativeEasterEggs";

import type { JSONSchema7 } from "json-schema";
import { useResolvedReportSchema } from "../../features/reports/hooks/useResolvedReportSchema";
import type { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import { useAuth } from "@workos-inc/authkit-react";

/**
 * Memoized wrapper around the heavy RJSF-driven structured-section view. Without this,
 * every CoReviewPage re-render (e.g. flipping the narrative-only toggle, staging a
 * comment) walks the full RJSF tree even when the form's actual props haven't changed.
 * Memoizing here lets toggling narrative-only stay snappy.
 */
const StructuredReportRender = memo(function StructuredReportRender({
  formData,
  jsonSchema,
  schemaMapping,
  stationId,
}: {
  formData: Record<string, unknown>;
  jsonSchema: JSONSchema7;
  schemaMapping: Record<string, unknown>;
  stationId?: string;
}) {
  return (
    <ExtractedInformation
      formData={formData}
      jsonSchema={jsonSchema}
      schemaMapping={schemaMapping as never}
      title="Officer's Report (read-only)"
      stationId={stationId}
    />
  );
});

function prettify(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

interface StructuredSection {
  key: string;
  label: string;
  sectionPath: string;
}

interface NarrativeEntry {
  index: number;
  text: string;
}

function getNarrativeText(entry: unknown): string {
  if (!entry || typeof entry !== "object") return "";
  const e = entry as Record<string, unknown>;
  if (typeof e.Text === "string") return e.Text;
  if (typeof e.Narrative === "string") return e.Narrative;
  return "";
}

// Sidebar section labels share one definition (overline + 700 was repeated
// per label; a token-level overline weight is a Phase 4 theme decision).
const sidebarLabelSx = { color: "text.secondary", fontWeight: 700 } as const;

const CoReviewPage: React.FC = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const { reportID } = useParams();
  const { user } = useAuth();
  const organizationId = useOrgId();
  // Warm the user-name cache so formatCommentAuthor can resolve display names.
  // Re-renders when the directory finishes loading so comment author labels fill in.
  useUserDirectory(organizationId ?? undefined);
  const toast = useToast();

  // Report data on react-query. Metadata + incident keep their deliberate
  // gcTime 0 (review state is mutable legal state — never serve it stale
  // across mounts); comments come from the shared co-review cache ("all"
  // cycles), mutated through the optimistic mutations below.
  const queryClient = useQueryClient();
  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID,
  });
  const incidentQuery = useQuery({
    ...incidentDataQuery(reportID ?? ""),
    enabled: !!reportID,
  });
  const metadata = metadataQuery.data ?? null;
  const incident = (incidentQuery.data?.reportContent ?? null) as Record<
    string,
    unknown
  > | null;
  // Current artifact versionNumber. Used to detect "new cycle, no CO comments
  // yet" — when the artifact has been resubmitted, its version outpaces any
  // existing comment's versionNumber and prior-cycle comments should hide.
  const artifactVersion =
    typeof incidentQuery.data?.versionNumber === "number"
      ? incidentQuery.data.versionNumber
      : null;

  const { comments: fetchedComments, error: commentsError } = useCoComments({
    reportId: reportID,
    cycle: "all",
  });
  // Cycle-detection atomicity: load() used to commit comments and
  // artifactVersion in one state batch. With independent queries, gate the
  // comments on the incident having loaded so deriveCycleInfo never sees
  // comments alongside artifactVersion=null — that would render prior-cycle
  // comments as current on a just-resubmitted report.
  const comments = useMemo(
    () => (incidentQuery.isSuccess ? fetchedComments : []),
    [incidentQuery.isSuccess, fetchedComments],
  );

  // Best-effort evidence fan-out so we can offer transcript download. Each
  // id resolves independently; a failed fetch yields null (logged) and never
  // fails the page — the old load() behaved the same.
  const evidenceIds = useMemo(
    () => metadata?.evidenceIds ?? [],
    [metadata?.evidenceIds],
  );
  const videoData = useQueries({
    queries: evidenceIds.map((id: string) => ({
      queryKey: ["coReview", "evidence", id] as const,
      queryFn: () =>
        getEvidenceData(id).catch((err) => {
          console.error("CoReview: getEvidenceData failed", id, err);
          return null;
        }),
      retry: false,
      staleTime: 0,
      gcTime: 0,
    })),
    combine: (results) =>
      results
        .map((q) => q.data)
        .filter((r): r is VideoData => Boolean(r)),
  });

  // One toast per error transition, preserving the load() failure copy.
  // Error checked off the query objects directly — never infer failure from
  // missing data (the error dispatch retains previous data).
  const loadError = metadataQuery.error || incidentQuery.error || commentsError;
  useEffect(() => {
    if (!loadError) return;
    const e = loadError as { response?: { data?: { error?: string } } };
    toast.error(
      e?.response?.data?.error ||
        "Failed to load report for review. Please try again.",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadError]);

  // Everything the CO stages before "Request changes" lives in one reducer
  // (section rejections, inline narrative comments, general comments, the
  // composer draft). The destructured names keep the render sites unchanged.
  const [staged, dispatchStaged] = useReducer(
    stagedFeedbackReducer,
    initialStagedFeedback,
  );
  const {
    sectionRejections,
    narrativeComments: pendingNarrativeComments,
    generalComments: pendingGeneralComments,
    generalDraft,
  } = staged;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  // The three review dialogs are mutually exclusive — one discriminant instead
  // of three booleans.
  const [activeDialog, setActiveDialog] = useState<
    "signature" | "reject" | "history" | null
  >(null);
  // Transcript-language download modal is orthogonal to the review dialogs, so
  // it keeps its own flag rather than joining the activeDialog discriminant.
  const [showTranscriptLanguageModal, setShowTranscriptLanguageModal] =
    useState(false);

  usePageHeader({
    title: "CO Review",
    subtitle: "Review the officer's report, reject sections or approve",
    icon: <LocalPoliceOutlinedIcon />,
  });

  const currentUser = useMemo(
    () => ({
      userId: user?.id ?? "co-demo-user",
      displayName:
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        user?.email ||
        "Commanding Officer",
    }),
    [user],
  );

  const resolveCommentMutation = useResolveCoComment(reportID ?? "");
  const unresolveCommentMutation = useUnresolveCoComment(reportID ?? "");
  const replyCommentMutation = useReplyToCoComment(reportID ?? "");

  // Review actions: plain mutations (no cache writes — approve refetches,
  // reject/commit navigate away). isPending replaces the old boolean flags.
  const approveMutation = useMutation({ mutationFn: coApprove });
  const requestChangesMutation = useMutation({ mutationFn: coRequestChanges });
  const rmsCommitMutation = useMutation({
    mutationFn: (reportId: string) => rmsCommitReport(reportId),
  });

  // Schema: the shared preview-parity pipeline (local $refs, KAR slice, no
  // oneOf→enum — this is a read-only render). The hook waits for the incident
  // before slicing KAR schemas. The raw schema comes from the
  // staleTime-Infinity reportSchemaQuery cache shared with
  // ExtractedInfoPage and FinalEdits.
  const schemaHook = useResolvedReportSchema({
    enabled: !!metadata,
    rms: metadata ? metadata.rms || "KAR" : null,
    incident: incident as EforceIncidentModel | null,
    karSlice: true,
    refFilter: "local",
    oneOfToEnum: false,
  });
  const jsonSchema = schemaHook.jsonSchema;
  const schemaMapping = schemaHook.schemaMapping;

  // Compute structured sections. We want to include EVERY top-level key in the data
  // that's a non-empty object/array (regardless of whether the schema declares its
  // type explicitly — schemas may use $ref/oneOf/anyOf and getSchemaObjectProperties
  // would skip those). Ordering follows JSON Schema property declaration order to
  // match EditSectionPage; any data-only keys are appended at the end.
  const sectionLayout = useMemo(() => {
    if (!incident) {
      return { list: [] as StructuredSection[], hasObjectSections: false };
    }
    const incidentKeys = Object.keys(incident);
    const schemaKeys = jsonSchema?.properties
      ? Object.keys(jsonSchema.properties)
      : [];
    const ordered = [
      ...schemaKeys.filter((k) => incidentKeys.includes(k)),
      ...incidentKeys.filter((k) => !schemaKeys.includes(k)),
    ];

    const objectSections: StructuredSection[] = ordered
      .filter((key) => key !== NARRATIVES_KEY)
      .filter((key) => {
        const v = incident[key];
        if (v === null || typeof v !== "object") return false;
        // Drop empty containers — there's nothing to scroll to or reject.
        if (Array.isArray(v) && v.length === 0) return false;
        if (!Array.isArray(v) && Object.keys(v).length === 0) return false;
        return true;
      })
      .map((key) => ({
        key,
        label: prettify(key),
        sectionPath: `$.${key}`,
      }));

    // Synthesize a "General Information" entry covering all top-level scalar
    // fields. LayoutGridField tags those collectively with
    // data-section-key="GeneralInformation".
    const hasScalars = ordered.some((key) => {
      if (key === NARRATIVES_KEY) return false;
      const v = incident[key];
      return v === null || typeof v !== "object";
    });
    const generalInfoSection: StructuredSection | null = hasScalars
      ? {
          key: "GeneralInformation",
          label: "General Information",
          sectionPath: "$.GeneralInformation",
        }
      : null;

    // Append a Narrative entry if the report has narratives.
    const narrativesArr = incident[NARRATIVES_KEY];
    const hasNarratives =
      Array.isArray(narrativesArr) && narrativesArr.length > 0;
    const narrativeSection: StructuredSection | null = hasNarratives
      ? {
          key: NARRATIVES_KEY,
          label: "Narrative",
          sectionPath: `$.${NARRATIVES_KEY}`,
        }
      : null;

    return {
      list: [
        ...(generalInfoSection ? [generalInfoSection] : []),
        ...objectSections,
        ...(narrativeSection ? [narrativeSection] : []),
      ],
      // narrativeOnly should reflect whether the report has any *real*
      // structured content beyond narratives. Synthetic entries (General
      // Information, Narrative) must NOT count here, otherwise we'd never
      // hide the structured form for narrative-only submissions.
      hasObjectSections: objectSections.length > 0,
    };
  }, [incident, jsonSchema]);

  const structuredSections = sectionLayout.list;
  const hasStructuredSections = sectionLayout.hasObjectSections;
  const narrativeOnly = !hasStructuredSections;

  const narrativeEntries: NarrativeEntry[] = useMemo(() => {
    if (!incident) return [];
    const arr = incident[NARRATIVES_KEY];
    if (!Array.isArray(arr)) return [];
    return arr.map((entry, index) => ({ index, text: getNarrativeText(entry) }));
  }, [incident]);

  const status = (metadata?.reportStatus ?? "") as ReportStatus | "";
  const isSigned = status === ReportStatus.IN_CO_REVIEW;
  const isApproved = status === ReportStatus.CO_APPROVED;
  const isReturned = status === ReportStatus.CHANGES_REQUESTED;
  // A legacy non-CO SIGNED report is not CO-reviewable (track-preserving model),
  // but this page is still reachable for one via Home-card navigation / direct
  // URL, so explain why no review actions are available.
  const isLegacySigned = status === ReportStatus.SIGNED;

  // A station "has an RMS" only when its rms vendor is a real external system.
  // Mirrors EditSection's convention where null/""/"KAR" mean there's nothing to
  // commit to (see useReportData.ts). When false, CO approval is terminal and the
  // "Commit to RMS" step is hidden.
  const hasRms = !["", "KAR"].includes(metadata?.rms ?? "");

  // Offset by the sticky app bar's height so the heading isn't hidden under it.
  // Sidebar uses `top: 80` for its sticky position, so the header is ~80px.
  const HEADER_OFFSET_PX = 86;

  const scrollElementIntoView = useCallback((el: Element | null) => {
    if (!el) return;
    const top =
      el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const scrollToSection = useCallback(
    (sectionKey: string) => {
      scrollElementIntoView(
        document.querySelector(`[data-section-key="${sectionKey}"]`),
      );
    },
    [scrollElementIntoView],
  );

  const scrollToNarrative = useCallback(
    (narrativeIndex: number) => {
      scrollElementIntoView(
        document.querySelector(`[data-co-narrative-index="${narrativeIndex}"]`),
      );
    },
    [scrollElementIntoView],
  );

  // The structured-form view should NOT render Narratives — we render them separately
  // below with NarrativeWithInlineComments so the CO can highlight text and leave inline
  // comments. Strip Narratives from both the schema and the formData fed to RJSF.
  // For narrative-only reports (no non-empty object sections) we additionally strip
  // object/array properties so the form renders only General Information (scalars)
  // without phantom empty section headers from the schema.
  const structuredOnlySchema = useMemo(() => {
    if (!jsonSchema?.properties) return jsonSchema;
    const props = { ...jsonSchema.properties };
    delete props[NARRATIVES_KEY];
    if (narrativeOnly) {
      for (const [key, propSchema] of Object.entries(props)) {
        const type =
          typeof propSchema === "object" && propSchema != null
            ? (propSchema as JSONSchema7).type
            : undefined;
        if (type === "object" || type === "array") {
          delete props[key];
        }
      }
    }
    return { ...jsonSchema, properties: props } as JSONSchema7;
  }, [jsonSchema, narrativeOnly]);

  const structuredOnlyIncident = useMemo(() => {
    if (!incident) return incident;
    const rest = { ...incident };
    delete rest[NARRATIVES_KEY];
    if (narrativeOnly) {
      for (const [key, value] of Object.entries(rest)) {
        if (value != null && typeof value === "object") {
          delete rest[key];
        }
      }
    }
    return rest;
  }, [incident, narrativeOnly]);

  // Cycle detection lives in deriveCycleInfo (extracted verbatim, unit-
  // pinned); `comments` above is gated on incident success so this never
  // runs against comments with a null artifactVersion.
  const { currentCycleComments, priorCycleComments } = deriveCycleInfo(
    comments,
    artifactVersion,
  );

  // Easter-egg detection across narratives
  const easterEggHit = useMemo(
    () => findEasterEggInNarratives(incident, organizationId),
    [incident, organizationId],
  );

  // Approve gating (spec §3)
  const stagedSomething =
    sectionRejections.length > 0 ||
    pendingNarrativeComments.length > 0 ||
    pendingGeneralComments.length > 0;
  // Replies inherit their parent's resolved state semantically; only the
  // parent's resolvedAt determines whether the issue is open.
  const unresolvedExistingCount = currentCycleComments.filter(
    (c) => !c.parentCommentId && !c.resolvedAt,
  ).length;
  const unresolvedPriorIssues = unresolvedExistingCount > 0;
  // FAIL CLOSED: the unresolved-comments and easter-egg gates below compute
  // against the loaded data — when the comments or incident fetch failed
  // they would evaluate against empty data and silently open. The old
  // all-or-nothing load() removed the action buttons on any failure; this
  // restores that property for the independent queries.
  const reviewDataFailed = !!commentsError || incidentQuery.isError;
  // The approve-eligibility predicate reads the optimistic cache; while a
  // comment action is in flight the server hasn't confirmed it yet.
  const commentActionPending =
    resolveCommentMutation.isPending ||
    unresolveCommentMutation.isPending ||
    replyCommentMutation.isPending;
  const approveDisabledReason: string | null = reviewDataFailed
    ? "Review data failed to load — retry or reload before taking action"
    : !isSigned
      ? "Report is not awaiting your review"
      : commentActionPending
        ? "Comment changes are still saving"
        : stagedSomething
          ? "You have staged comments or section rejections — clear them or send back instead"
          : easterEggHit
            ? `Easter-egg phrase "${easterEggHit.phrase}" still in narrative ${easterEggHit.narrativeIndex + 1}`
            : unresolvedPriorIssues
              ? "Some comments from prior cycles are still unresolved"
              : null;

  // Reject opens a confirmation modal — allow whenever the CO has either
  // staged new feedback this session OR there are unresolved comments from
  // the current cycle (post-retract: same feedback, send it back to the
  // officer without forcing the CO to type a new comment).
  const rejectDisabledReason: string | null = reviewDataFailed
    ? "Review data failed to load — retry or reload before taking action"
    : !isSigned
      ? "Report is not awaiting your review"
      : !stagedSomething && !unresolvedPriorIssues
        ? "Stage at least one rejection or comment first"
        : null;

  // ----- staged feedback handlers (thin dispatch wrappers; the replace-by-
  // section, trim-and-ignore-empty etc. semantics live in the reducer) -----
  const stageSectionRejection = (sec: StructuredSection, text: string) =>
    dispatchStaged({ type: "stageSectionRejection", section: sec, text });

  const editSectionRejection = (pendingId: string, text: string) =>
    dispatchStaged({ type: "editSectionRejection", pendingId, text });

  const clearSectionRejection = (pendingId: string) =>
    dispatchStaged({ type: "clearSectionRejection", pendingId });

  const stageNarrativeComment = (input: {
    narrativeIndex: number;
    charStart: number;
    charEnd: number;
    quotedText: string;
    commentText: string;
  }) => dispatchStaged({ type: "stageNarrativeComment", ...input });

  const deletePendingNarrativeComment = (pendingId: string) =>
    dispatchStaged({ type: "deleteNarrativeComment", pendingId });

  const addGeneralComment = (text: string) =>
    dispatchStaged({ type: "addGeneralComment", text });

  const editGeneralComment = (pendingId: string, text: string) =>
    dispatchStaged({ type: "editGeneralComment", pendingId, text });

  const deleteGeneralComment = (pendingId: string) =>
    dispatchStaged({ type: "deleteGeneralComment", pendingId });

  const setGeneralDraft = (text: string) =>
    dispatchStaged({ type: "setGeneralDraft", text });

  // ----- reject report -----
  const handleRejectReport = async () => {
    if (!reportID) return;
    // toPendingCoComments tags every staged comment with the CO's display
    // name so officers (who can't read the user directory) still see who
    // wrote each comment.
    const payload: PendingCoComment[] = toPendingCoComments(
      staged,
      currentUser.displayName,
    );
    // Empty payload is allowed only when there are unresolved existing
    // comments to re-send to the officer (post-retract). Otherwise refuse
    // — backend would accept it, but it's a meaningless reject.
    if (payload.length === 0 && !unresolvedPriorIssues) return;
    try {
      await requestChangesMutation.mutateAsync({
        reportId: reportID,
        comments: payload,
      });
      dispatchStaged({ type: "clearAll" });
      setActiveDialog(null);
      toast.success("Report rejected and sent to officer with comments.");
      navigate("/co-review");
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to reject report.");
    }
  };

  // ----- approve -----
  const handleApprove = async () => {
    if (!reportID) return;
    try {
      await approveMutation.mutateAsync({ reportId: reportID });
      toast.success(
        hasRms ? "Report approved. Ready for RMS commit." : "Report approved.",
      );
      setActiveDialog(null);
      // The old `await load()`: refetch keeps the previous data visible
      // during the round trip, exactly like load() did.
      await Promise.all([
        metadataQuery.refetch(),
        incidentQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: coReviewKeys.comments(reportID),
        }),
      ]);
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to approve report.");
    }
  };

  // ----- RMS / download -----
  const handleRmsCommit = async () => {
    if (!reportID) return;
    try {
      const res = await rmsCommitMutation.mutateAsync(reportID);
      toast.success("Report committed to RMS.");
      navigate(
        `/confirmation/${reportID}` +
          (res.eventNumber
            ? `?eventNumber=${encodeURIComponent(res.eventNumber)}`
            : ""),
      );
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to commit report to RMS.");
    }
  };

  // Comment actions go through the shared optimistic mutations (cache patch
  // + rollback + toast live there). Handlers stay async so awaiting callers
  // resolve after the server settles; rejections are swallowed because the
  // mutation already toasted.
  const handleResolveComment = async (commentId: string) => {
    if (!reportID) return;
    await resolveCommentMutation
      .mutateAsync({ commentId, resolvedByUserId: user?.id ?? null })
      .catch(() => undefined);
  };

  const handleUnresolveComment = async (commentId: string) => {
    if (!reportID) return;
    await unresolveCommentMutation
      .mutateAsync({ commentId, resolvedByUserId: null })
      .catch(() => undefined);
  };

  const handleReplyComment = async (parentCommentId: string, text: string) => {
    if (!reportID) return;
    const parent = comments.find((c) => c.commentId === parentCommentId);
    if (!parent) {
      toast.error("Cannot reply: parent comment is no longer visible.");
      return;
    }
    await replyCommentMutation
      .mutateAsync({ parent, text, author: currentUser })
      .catch(() => undefined);
  };

  const handleDownload = async () => {
    if (!reportID) return;
    try {
      await downloadReportPdf({ reportId: reportID });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to download report.");
    }
  };

  // ----- comments grouped by narrative for inline display -----
  // Only current-cycle comments render inline; prior-cycle ones are accessible
  // via the "View review history" dialog so the CO can focus on this cycle's
  // open items without the noise of resolved-in-prior-cycle threads.
  const narrativeCommentsByIndex = useMemo(() => {
    const map = new Map<number, CoReviewComment[]>();
    for (const c of currentCycleComments) {
      if (typeof c.narrativeIndex !== "number") continue;
      const list = map.get(c.narrativeIndex) ?? [];
      list.push(c);
      map.set(c.narrativeIndex, list);
    }
    return map;
  }, [currentCycleComments]);

  const pendingNarrativeByIndex = useMemo(() => {
    const map = new Map<number, PendingNarrativeComment[]>();
    for (const p of pendingNarrativeComments) {
      const list = map.get(p.narrativeIndex) ?? [];
      list.push(p);
      map.set(p.narrativeIndex, list);
    }
    return map;
  }, [pendingNarrativeComments]);

  // ----- section-rejection comments (existing) by section -----
  const sectionRejectionCommentsByKey = useMemo(() => {
    const map = new Map<string, CoReviewComment[]>();
    for (const c of currentCycleComments) {
      if (!c.isSectionRejection) continue;
      // sectionPath like "$.Subjects" → key "Subjects"
      const key = c.sectionPath.replace(/^\$\./, "");
      const list = map.get(key) ?? [];
      list.push(c);
      map.set(key, list);
    }
    return map;
  }, [currentCycleComments]);

  // Index staged (pending) section rejections by section key so the section
  // list render does an O(1) lookup per section instead of a linear scan.
  const sectionRejectionsByKey = useMemo(
    () => new Map(sectionRejections.map((r) => [r.sectionKey, r] as const)),
    [sectionRejections],
  );

  // ----- existing general comments (sectionPath "$", not a section
  //       rejection). Includes replies so threads stay intact when rendered
  //       through SectionCommentRail. -----
  const existingGeneralComments = useMemo(
    () =>
      currentCycleComments.filter(
        (c) => c.sectionPath === "$" && !c.isSectionRejection,
      ),
    [currentCycleComments],
  );

  // ----- prior-cycle history grouped by cycle -----
  const priorCyclesByNumber = useMemo(() => {
    const map = new Map<number, CoReviewComment[]>();
    for (const c of priorCycleComments) {
      const list = map.get(c.reviewCycle) ?? [];
      list.push(c);
      map.set(c.reviewCycle, list);
    }
    return new Map([...map.entries()].sort((a, b) => b[0] - a[0]));
  }, [priorCycleComments]);

  const sidebar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflow: "hidden",
      }}
    >
      <Box>
        <Typography variant="overline" sx={sidebarLabelSx}>
          Status
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {prettyStatus(status, hasRms)}
        </Typography>
        {metadata?.reviewedAt &&
          (status === ReportStatus.CO_APPROVED ||
            status === ReportStatus.CHANGES_REQUESTED) && (
            <Typography variant="caption" color="text.secondary" display="block">
              {status === ReportStatus.CO_APPROVED ? "Approved" : "Rejected"}{" "}
              {dayjs(metadata.reviewedAt).format("MMM D, h:mm A")}
              {(() => {
                const name =
                  metadata.reviewedByName ||
                  (metadata.reviewedBy ? getUserName(metadata.reviewedBy) : "");
                return name ? ` by ${name}` : "";
              })()}
            </Typography>
          )}
        {metadata?.signedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Signed {dayjs(metadata.signedAt).format("MMM D, h:mm A")}
          </Typography>
        )}
      </Box>

      {easterEggHit && isSigned && (
        <Alert severity="warning" icon={<ReportProblemOutlinedIcon fontSize="small" />}>
          Easter-egg phrase &quot;<strong>{easterEggHit.phrase}</strong>&quot; still
          present in narrative {easterEggHit.narrativeIndex + 1}. Reject so the officer
          can remove it.
        </Alert>
      )}

      {/* Scrollable middle region — collects every list-y block. Without this, lots of
          staged comments + history would push the action buttons below the sidebar's
          height and clip them. */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Reserve a hair of inset so the scroll thumb doesn't crowd content.
          pr: 0.5,
        }}
      >

      {/* Per-section reject controls — hidden in narrative-only mode AND when the
          report has no structured sections at all. */}
      {isSigned && !narrativeOnly && (
        <Box>
          <Typography variant="overline" sx={sidebarLabelSx}>
            Sections to review
          </Typography>
          <Stack spacing={1} mt={0.5}>
            {structuredSections.map((sec) => {
              const staged = sectionRejectionsByKey.get(sec.key);
              return (
                <SectionRejectControl
                  key={sec.key}
                  sectionLabel={sec.label}
                  sectionPath={sec.sectionPath}
                  staged={staged ? { pendingId: staged.pendingId, commentText: staged.commentText } : null}
                  active={isSigned}
                  onStage={(text) => stageSectionRejection(sec, text)}
                  onEdit={editSectionRejection}
                  onClear={clearSectionRejection}
                  onJumpToSection={() => scrollToSection(sec.key)}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* General comment composer + staged list. Lives in the sidebar so the CO can
          stage cross-cutting suggestions without opening a dialog. */}
      {isSigned && (
        <Box>
          <Typography
            variant="overline"
            sx={{ ...sidebarLabelSx, display: "block" }}
          >
            General comments
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Big-picture feedback or suggestions not tied to a specific section.
          </Typography>
          <TextField
            size="small"
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            placeholder="Suggestion or comment for the officer..."
            value={generalDraft}
            onChange={(e) => setGeneralDraft(e.target.value)}
          />
          <Box display="flex" justifyContent="flex-end" mt={0.5}>
            <Button
              size="small"
              startIcon={<AddCommentOutlinedIcon />}
              disabled={!generalDraft.trim()}
              onClick={() => {
                addGeneralComment(generalDraft);
                setGeneralDraft("");
              }}
            >
              Stage comment
            </Button>
          </Box>

          {pendingGeneralComments.length > 0 && (
            <Stack spacing={1} mt={1}>
              {pendingGeneralComments.map((g) => (
                <Paper
                  key={g.pendingId}
                  elevation={0}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: `1px dashed ${theme.palette.info.main}`,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={g.commentText}
                    onChange={(e) => editGeneralComment(g.pendingId, e.target.value)}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => deleteGeneralComment(g.pendingId)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Existing general comments (sectionPath "$") — visible whenever any
          exist so the CO can resolve / reply to their own cross-cutting
          feedback after a retract or while CHANGES_REQUESTED. */}
      {existingGeneralComments.length > 0 && (
        <Box>
          <Typography variant="overline" sx={sidebarLabelSx}>
            General comments this cycle
          </Typography>
          <Stack spacing={1} mt={0.5}>
            <SectionCommentRail
              sectionPath="$"
              fieldLabel="General"
              comments={existingGeneralComments}
              mode="co-edit"
              reportOwnerUserId={metadata?.userId}
              onResolve={handleResolveComment}
              onUnresolve={handleUnresolveComment}
              onReply={handleReplyComment}
            />
          </Stack>
        </Box>
      )}

      {/* Existing section comments from the current review cycle — visible
          whenever there are any so the CO can resolve / reply to their own
          feedback. Renders for both IN_CO_REVIEW (e.g. after a retract — old
          comments still apply) and CHANGES_REQUESTED. */}
      {sectionRejectionCommentsByKey.size > 0 && (
        <Box>
          <Typography variant="overline" sx={sidebarLabelSx}>
            Sections rejected this cycle
          </Typography>
          <Stack spacing={1} mt={0.5}>
            {Array.from(sectionRejectionCommentsByKey.entries()).map(([key, items]) => (
              <SectionCommentRail
                key={key}
                sectionPath={items[0]?.sectionPath ?? `$.${key}`}
                fieldLabel={prettify(key)}
                comments={items}
                mode="co-edit"
                reportOwnerUserId={metadata?.userId}
                onResolve={handleResolveComment}
                onUnresolve={handleUnresolveComment}
                onReply={handleReplyComment}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Narrative inline comments staged this session */}
      {pendingNarrativeComments.length > 0 && (
        <Box>
          <Typography variant="overline" sx={sidebarLabelSx}>
            Inline narrative comments staged ({pendingNarrativeComments.length})
          </Typography>
          <Stack spacing={1} mt={0.5}>
            {pendingNarrativeComments.map((p) => (
              <Paper
                key={p.pendingId}
                elevation={0}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: `1px dashed ${theme.palette.warning.main}`,
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                }}
              >
                <Typography
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => scrollToNarrative(p.narrativeIndex)}
                  sx={{
                    color: "text.secondary",
                    background: "none",
                    border: 0,
                    p: 0,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Narrative {p.narrativeIndex + 1}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    color: "text.secondary",
                    borderLeft: `2px solid ${theme.palette.warning.main}`,
                    pl: 1,
                    my: 0.5,
                  }}
                >
                  &ldquo;{p.quotedText}&rdquo;
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {p.commentText}
                </Typography>
                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => deletePendingNarrativeComment(p.pendingId)}
                  >
                    Remove
                  </Button>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Review history (status transitions + comments by cycle, spec §7) */}
      {priorCyclesByNumber.size > 0 && (
        <Button
          startIcon={<HistoryIcon />}
          variant="outlined"
          onClick={() => setActiveDialog("history")}
          fullWidth
        >
          View review history ({priorCyclesByNumber.size} prior {priorCyclesByNumber.size === 1 ? "cycle" : "cycles"})
        </Button>
      )}

      </Box>
      {/* End scrollable middle region */}

      {/* Action buttons — pinned at the bottom so they never get clipped */}
      <Stack spacing={1} sx={{ flexShrink: 0 }}>
        {hasAnyTranscript(videoData) && (
          <Button
            variant="outlined"
            startIcon={<SubtitlesIcon />}
            onClick={() => setShowTranscriptLanguageModal(true)}
            fullWidth
          >
            Download transcript
          </Button>
        )}
        {isSigned && (
          <>
            <Tooltip
              title={approveDisabledReason ?? "Approve and sign"}
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<VerifiedOutlinedIcon />}
                  onClick={() => setActiveDialog("signature")}
                  disabled={!!approveDisabledReason}
                  fullWidth
                >
                  Approve report
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={rejectDisabledReason ?? "Send back to officer"}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ReportProblemOutlinedIcon />}
                  onClick={() => setActiveDialog("reject")}
                  loading={requestChangesMutation.isPending}
                  disabled={!!rejectDisabledReason}
                  fullWidth
                >
                  Reject report
                  {sectionRejections.length +
                    pendingNarrativeComments.length +
                    pendingGeneralComments.length >
                  0
                    ? ` (${
                        sectionRejections.length +
                        pendingNarrativeComments.length +
                        pendingGeneralComments.length
                      })`
                    : ""}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {isApproved && (
          <>
            {hasRms ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRmsCommit}
                loading={rmsCommitMutation.isPending}
                fullWidth
              >
                Commit to RMS
              </Button>
            ) : (
              <Alert severity="success" sx={{ width: "100%" }}>
                This report is approved. No RMS is configured for this station, so
                no further commit step is required.
              </Alert>
            )}
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownload}
              fullWidth
            >
              Download PDF
            </Button>
          </>
        )}
        {isReturned && (
          <Alert severity="info" sx={{ width: "100%" }}>
            This report has been sent back to the officer. You&apos;ll see it in your queue
            again once they resubmit.
          </Alert>
        )}
        {isLegacySigned && (
          <Alert severity="info" sx={{ width: "100%" }}>
            This report was signed in the non-CO-review workflow, so it isn&apos;t part of CO
            review — there&apos;s nothing to approve or reject here.
          </Alert>
        )}
      </Stack>
    </Paper>
  );

  return (
    <>
      <DownloadTranscriptLanguageModal
        open={showTranscriptLanguageModal}
        onClose={() => setShowTranscriptLanguageModal(false)}
        onDownload={async (languageCode) => {
          try {
            await downloadCombinedTranscriptInLanguage(
              videoData,
              `transcript-${reportID ?? "report"}`,
              languageCode,
            );
            setShowTranscriptLanguageModal(false);
          } catch (error) {
            console.error("Failed to download transcript:", error);
            toast.error("Failed to translate the transcript. Please try again.");
          }
        }}
      />

      <SignatureDialog
        open={activeDialog === "signature"}
        mode="coApprove"
        signerName={currentUser.displayName}
        onClose={() => setActiveDialog(null)}
        onConfirm={handleApprove}
        loading={approveMutation.isPending}
      />

      <RejectConfirmationDialog
        open={activeDialog === "reject"}
        sectionRejectionLabels={sectionRejections.map((r) => r.sectionLabel)}
        narrativeCommentCount={pendingNarrativeComments.length}
        generalCommentCount={pendingGeneralComments.length}
        existingUnresolvedCount={unresolvedExistingCount}
        onClose={() => setActiveDialog(null)}
        onConfirm={handleRejectReport}
        loading={requestChangesMutation.isPending}
      />

      {reportID && (
        <ReviewHistoryDialog
          open={activeDialog === "history"}
          reportId={reportID}
          onClose={() => setActiveDialog(null)}
        />
      )}

      <Container maxWidth="xl" sx={{ pt: 2, pb: 4 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            {schemaHook.isError && !jsonSchema ? (
              // Previously a schema failure console.errored and left this
              // skeleton up forever; surface it with a retry instead
              // (ExtractedInfoPage's pattern).
              <ErrorState
                variant="panel"
                message="Failed to load the report form for review."
                onRetry={() => schemaHook.retry()}
              />
            ) : !incident || !jsonSchema || !schemaMapping ? (
              <Skeleton variant="rounded" height={520} />
            ) : (
              <Stack spacing={3}>
                {/* Structured report sections — read-only display. Narratives are
                    stripped from the schema/data here and rendered separately below
                    with NarrativeWithInlineComments so the CO can highlight to comment.
                    For narrative-only reports the schema/data fed in here is
                    pre-filtered to scalar properties so only General Information
                    renders (no phantom empty object sections). */}
                <StructuredReportRender
                  formData={structuredOnlyIncident as Record<string, unknown>}
                  jsonSchema={structuredOnlySchema as JSONSchema7}
                  schemaMapping={schemaMapping}
                  stationId={organizationId ?? undefined}
                />

                {/* Narrative section, with inline range-highlight comments. Rendered
                    after the structured form so it lands in roughly the schema-defined
                    position (Narratives is the last top-level property). */}
                {narrativeEntries.length > 0 && (
                  <Paper data-section-key={NARRATIVES_KEY} elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Narrative
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                      Highlight any text below to leave an inline comment for the officer.
                    </Typography>
                    <Stack spacing={2}>
                      {narrativeEntries.map((n) => (
                        <Box key={n.index} data-co-narrative-index={n.index}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Narrative {n.index + 1}
                          </Typography>
                          <NarrativeWithInlineComments
                            narrativeIndex={n.index}
                            text={n.text}
                            comments={narrativeCommentsByIndex.get(n.index) ?? []}
                            pendingComments={pendingNarrativeByIndex.get(n.index) ?? []}
                            mode={isSigned ? "co-edit" : "co-readonly"}
                            onAddComment={stageNarrativeComment}
                            onResolveComment={handleResolveComment}
                            onUnresolveComment={handleUnresolveComment}
                            onReplyComment={handleReplyComment}
                            reportOwnerUserId={metadata?.userId}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            )}
          </Grid>
          {isLgUp ? (
            <Grid size={{ xs: 12, lg: 4 }}>
              <Box sx={{ position: "sticky", top: 80, height: "calc(100vh - 100px)" }}>
                {sidebar}
              </Box>
            </Grid>
          ) : (
            <>
              <Box
                sx={{
                  position: "fixed",
                  bottom: 16,
                  right: 16,
                  zIndex: 1300,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<LocalPoliceOutlinedIcon />}
                  onClick={() => setSidebarOpen(true)}
                >
                  Review actions
                </Button>
              </Box>
              <Drawer
                anchor="right"
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
              >
                <Box display="flex" justifyContent="space-between" p={1}>
                  <Typography variant="h6">Review</Typography>
                  <IconButton
                    aria-label="Close review panel"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ p: 1.5, height: "100%" }}>{sidebar}</Box>
              </Drawer>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
};

function prettyStatus(status: string, hasRms: boolean): string {
  switch (status) {
    case ReportStatus.IN_CO_REVIEW:
      return "Awaiting your review";
    case ReportStatus.CO_APPROVED:
      return hasRms ? "Approved · ready for RMS" : "Approved";
    case ReportStatus.CHANGES_REQUESTED:
      return "Rejected · awaiting officer";
    case ReportStatus.RMS_COMMITTED:
      return "Submitted to RMS";
    case ReportStatus.PENDING_REVIEW:
      return "Officer drafting";
    default:
      return status || "Loading…";
  }
}

export default CoReviewPage;
